/**
 * RecentSignups.jsx (iter157) — Superadmin view of the newest Stockaris tenants.
 *
 * Use cases:
 *   • Spot onboarding issues fast (a customer signs up → notice them here in
 *     the same minute → jump to Grant Add-on if they need a comp).
 *   • Audit recent self-serve vs. admin-created tenants.
 *
 * Also surfaced as a compact widget on the Dashboard (superadmin only) —
 * see DashboardRecentSignups.jsx for the embedded variant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { UserPlus, Crown, EnvelopeSimple, Buildings, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: 7, label: '7 days' },
  { key: 30, label: '30 days' },
  { key: 90, label: '90 days' },
  { key: 0, label: 'All time' },
];

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-600',
    trialing: 'bg-amber-500',
    pending_payment: 'bg-rose-600',
    paused: 'bg-zinc-500',
    cancelled: 'bg-zinc-700',
  };
  return <Badge className={`${map[status] || 'bg-zinc-500'} text-[10px]`}>{status || '—'}</Badge>;
}

function relative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function SignupSidePanel({ row, onClose, onGrant }) {
  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="signup-side-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Buildings size={20} weight="duotone" className="text-brand" />
            {row.name}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{row.slug}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="grid grid-cols-[110px_1fr] gap-y-2 text-xs">
            <div className="text-muted-foreground uppercase tracking-widest">Plan</div>
            <div className="font-mono uppercase">{row.plan_key}{row.billing_cycle ? ` · ${row.billing_cycle}` : ''}</div>

            <div className="text-muted-foreground uppercase tracking-widest">Status</div>
            <div><StatusBadge status={row.status} /></div>

            <div className="text-muted-foreground uppercase tracking-widest">Country</div>
            <div className="font-mono">{row.country || '—'}</div>

            <div className="text-muted-foreground uppercase tracking-widest">Created</div>
            <div className="font-mono">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'} <span className="text-muted-foreground">({relative(row.created_at)})</span></div>

            <div className="text-muted-foreground uppercase tracking-widest">Add-ons</div>
            <div className="font-mono text-xs">{row.addons?.length ? row.addons.join(', ') : '—'}</div>

            {row.manually_created_by && (
              <>
                <div className="text-muted-foreground uppercase tracking-widest">Created by</div>
                <div className="font-mono text-xs">admin · {row.manually_created_by}</div>
              </>
            )}
          </div>

          {row.owner && (
            <div className="border-t border-border pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Owner</div>
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={14} weight="bold" className="text-brand" />
                <code className="font-mono text-xs break-all">{row.owner.email}</code>
              </div>
              <div className="text-xs text-muted-foreground ml-6">{row.owner.name}</div>
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-3 border-t border-border">
          <Button variant="outline" onClick={onClose} className="flex-1">Close</Button>
          <Button onClick={() => onGrant(row)} className="flex-1 bg-brand hover:bg-brand/90 text-white" data-testid="signup-side-panel-grant">
            <Crown size={14} className="mr-1" /> Grant add-on
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RecentSignupsContent({ embedded = false, defaultDays = 7, defaultLimit = 20 }) {
  const [days, setDays] = useState(defaultDays);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/billing/recent-signups`, {
        params: { days: days || 0, limit: defaultLimit },
        withCredentials: true,
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total ?? 0);
    } catch (e) {
      setItems([]);
      setTotal(0);   // iter157 — reset count so the chip doesn't show a stale value on error
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const goGrant = (row) => {
    // Pass via URL — Grant Add-on page can pre-select using the email lookup
    if (row?.owner?.email) navigate(`/admin/grant-addon?email=${encodeURIComponent(row.owner.email)}`);
    else navigate('/admin/grant-addon');
  };

  return (
    <div data-testid="recent-signups-content">
      <div className="flex items-center gap-2 mb-3">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setDays(r.key)}
            data-testid={`recent-signups-range-${r.key}`}
            className={`text-xs px-3 py-1 border ${days === r.key ? 'border-brand bg-brand/10 font-bold' : 'border-border hover:border-foreground/40'}`}
          >
            {r.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground" data-testid="recent-signups-count">
          {loading ? 'Loading…' : `${total} signup${total === 1 ? '' : 's'} in window`}
        </div>
      </div>

      {!loading && items.length === 0 ? (
        <div className="text-sm text-muted-foreground p-8 border border-dashed border-border text-center" data-testid="recent-signups-empty">
          No signups in this window.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-left p-2">Owner</th>
                <th className="text-left p-2">Signed up</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="border-t border-border cursor-pointer hover:bg-brand/5"
                  data-testid={`recent-signup-row-${r.id}`}
                >
                  <td className="p-2">
                    <div className="font-bold text-xs">{r.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.slug}</div>
                  </td>
                  <td className="p-2 text-xs uppercase font-bold font-mono">{r.plan_key}</td>
                  <td className="p-2 text-xs">
                    {r.owner ? (
                      <>
                        <div className="font-mono">{r.owner.email}</div>
                        <div className="text-muted-foreground">{r.owner.name}</div>
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-2 text-xs">
                    <div className="font-mono">{relative(r.created_at)}</div>
                    {r.manually_created_by && <div className="text-[10px] text-muted-foreground">via admin · {r.manually_created_by}</div>}
                  </td>
                  <td className="p-2"><StatusBadge status={r.status} /></td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); goGrant(r); }}
                      data-testid={`recent-signup-grant-${r.id}`}
                      title="Grant add-on"
                    >
                      <Crown size={14} className="text-amber-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SignupSidePanel row={selected} onClose={() => setSelected(null)} onGrant={(r) => { setSelected(null); goGrant(r); }} />
    </div>
  );
}

export default function RecentSignups() {
  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="recent-signups-page">
        <div className="flex items-center gap-3 mb-1">
          <UserPlus size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Recent Signups</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Newest tenants who signed up to Stockaris. Click a row for details, or jump straight to grant an add-on.
        </p>
        <RecentSignupsContent defaultDays={7} defaultLimit={20} />
      </div>
    </Layout>
  );
}
